import React, { useEffect, useMemo, useRef, useState } from "react";
import MenuItem from "@mui/material/MenuItem";
import { SelectChangeEvent } from "@mui/material/Select";
import {
  FullscreenControl,
  GeolocationControl,
  Map as YMap,
  Placemark,
  RouteButton,
  YMaps,
} from "@pbe/react-yandex-maps";
import {
  BackgroundContainer,
  CAddress,
  CContainer,
  CItem,
  CList,
  CListWrapper,
  CMap,
  CName,
  CCityTitle,
  CEmptyState,
  CityFilterContainer,
  CitySelect,
  CitySelectLabel,
  GraveAddress,
  GraveDivider,
  Graveyard,
  GraveyardName,
} from "./styles";

const tableExample = [
  {
    id: 1,
    name: "Салон ритуальных услуг Похоронный Дом",
    email: "test1@mail.ru",
    phone: "+7 999 999 99 99",
    coordinates: [68.9539819, 33.1066582],
    selected: false,
    city: "Мурманск",
  },
  {
    id: 2,
    name: 'Салон Ритуальных Услуг "Последний путь"',
    email: "test2@mail.ru",
    phone: "+7 999 999 99 99",
    coordinates: [68.9528438, 33.1058195],
    selected: false,
    city: "Мурманск",
  },
  {
    id: 3,
    name: 'ООО "Городской Центр Ритуальных Услуг"',
    email: "test3@mail.ru",
    phone: "+7 999 999 99 99",
    coordinates: [68.9552536, 33.105201],
    selected: false,
    city: "Мурманск",
  },
  {
    id: 4,
    name: 'ООО "Городской Центр Ритуальных Услуг"',
    email: "test3@mail.ru",
    phone: "+7 999 999 99 99",
    coordinates: [68.9552536, 33.105201],
    selected: false,
    city: "Мурманск",
  },
  {
    id: 5,
    name: "Ритуал Север",
    email: "test5@mail.ru",
    phone: "+7 999 888 77 66",
    coordinates: [69.071873, 33.423095],
    selected: false,
    city: "Североморск",
  },
];

interface ICompany {
  id: number;
  name: string;
  email: string;
  phone: string;
  coordinates: number[];
  selected: boolean;
  city: string;
}

const CompaniesPage = () => {
  const ref: any = useRef();
  const [coords, setNewCoords] = React.useState<number[]>([
    68.9539819, 33.1066582,
  ]);
  const [companies, setCompanies] = useState<ICompany[]>([]);
  const [selectedCity, setSelectedCity] = useState<string>("");
  const cities = useMemo(() => {
    const uniqueCities = Array.from(
      new Set(companies.map((company) => company.city))
    );
    return uniqueCities.sort((a, b) => a.localeCompare(b));
  }, [companies]);
  const filteredCompanies = useMemo(() => {
    if (!selectedCity) {
      return companies;
    }
    return companies.filter((company) => company.city === selectedCity);
  }, [companies, selectedCity]);
  const companiesByCity = useMemo(() => {
    const grouped = new Map<string, ICompany[]>();
    filteredCompanies.forEach((company) => {
      if (!grouped.has(company.city)) {
        grouped.set(company.city, []);
      }
      grouped.get(company.city)!.push(company);
    });
    const groupedEntries: [string, ICompany[]][] = Array.from(
      grouped.entries()
    );
    return groupedEntries.map(([city, cityCompanies]) => ({
      city,
      companies: cityCompanies,
    }));
  }, [filteredCompanies]);
  const setCenter = (id: number) => {
    const item = companies.find((item) => item.id === id);
    if (item) {
      if (ref.current) {
        const obj = ref.current.getMap().geoObjects;
        obj.each((geo: any) => {
          if (
            geo.geometry._coordinates[0] === item.coordinates[0] &&
            geo.geometry._coordinates[1] === item.coordinates[1]
          ) {
            geo.balloon.open();
          }
        });
      }
      setNewCoords(item.coordinates);
      setCompanies((prevCompanies) =>
        prevCompanies.map((prevCompany) => ({
          ...prevCompany,
          selected: prevCompany.id === id,
        }))
      );
    }
  };

  const selectCity = (
    event: SelectChangeEvent<unknown>,
    _child: React.ReactNode
  ) => setSelectedCity(event.target.value as string);

  const mapState = {
    center: tableExample[0]?.coordinates || [55.751574, 37.573856],
    zoom: 15,
    controls: [],
  };
  useEffect(() => {
    const loadData = async () => {
      setCompanies(tableExample);
    };
    loadData();
  }, []);
  useEffect(() => {
    if (companies.length === 0) {
      return;
    }
    if (!selectedCity) {
      const selectedCompany =
        companies.find((company) => company.selected) || companies[0];
      if (selectedCompany && !selectedCompany.selected) {
        setCenter(selectedCompany.id);
      } else if (selectedCompany) {
        setNewCoords(selectedCompany.coordinates);
      }
      return;
    }
    const cityCompanies = companies.filter(
      (company) => company.city === selectedCity
    );
    if (cityCompanies.length === 0) {
      return;
    }
    const selectedCompany = cityCompanies.find((company) => company.selected);
    if (!selectedCompany) {
      setCenter(cityCompanies[0].id);
      return;
    }
    setNewCoords(selectedCompany.coordinates);
  }, [companies, selectedCity]);

  return (
    <>
      <BackgroundContainer
        style={{
          marginTop: "120px",
          paddingTop: "20px",
          paddingBottom: "20px",
        }}
      >
        <Graveyard>
          <GraveyardName>Компании ритуальных услуг</GraveyardName>
          <GraveDivider />
          <GraveAddress>
            Для ритуальных услуг вы можете воспользоваться следующими
            компаниями:
          </GraveAddress>
        </Graveyard>
        <CContainer>
          <CListWrapper>
            <CityFilterContainer>
              <CitySelectLabel
                id="company-city-filter-label"
                component="label"
                htmlFor="company-city-filter"
              >
                Выберите город
              </CitySelectLabel>
              <CitySelect
                id="company-city-filter"
                value={selectedCity}
                onChange={selectCity}
                labelId="company-city-filter-label"
                displayEmpty
                renderValue={(value) =>
                  value ? (value as string) : "Все города"
                }
                variant="outlined"
                size="small"
              >
                <MenuItem value="">Все города</MenuItem>
                {cities.map((city) => (
                  <MenuItem key={city} value={city}>
                    {city}
                  </MenuItem>
                ))}
              </CitySelect>
            </CityFilterContainer>
            <CList>
              {companiesByCity.length > 0 ? (
                companiesByCity.map(({ city, companies: cityCompanies }) => (
                  <React.Fragment key={city}>
                    <CCityTitle variant="subtitle2">{city}</CCityTitle>
                    {cityCompanies.map((item, index) => (
                      <CItem
                        key={`company-${city}-${index}-${item.email}-${item.phone}`}
                        onClick={() => setCenter(item.id)}
                      >
                        <CName>{item.name}</CName>
                        <CAddress>
                          Контактные данные:
                          <br /> Номер телефона:{" "}
                          <a href={`tel:${item.phone}`}>{item.phone}</a>
                          <br /> Email:{" "}
                          <a href={`mailto:${item.email}`}>{item.email}</a>
                        </CAddress>
                      </CItem>
                    ))}
                  </React.Fragment>
                ))
              ) : (
                <CEmptyState>
                  Компании не найдены для выбранного города
                </CEmptyState>
              )}
            </CList>
          </CListWrapper>
          <CMap>
            <YMaps>
              <YMap
                state={{
                  ...mapState,
                  center: coords,
                }}
                width="100%"
                height="440px"
              >
                {filteredCompanies?.length > 0 &&
                  filteredCompanies.map((item, index) => (
                    <Placemark
                      key={index}
                      modules={["geoObject.addon.balloon"]}
                      geometry={item.coordinates}
                      properties={{
                        balloonContentHeader: item.name,
                        balloonContentBody: `<p>Контактные данные:<br> Номер телефона: <a href="tel:${item.phone}">${item.phone}</a><br>  Email: <a href="tel:${item.email}">${item.email}</a><br></p>`,
                      }}
                      instanceRef={ref}
                      onLoad={(e: any) => {
                        if (item.selected) {
                          e?.balloon?.open();
                        }
                      }}
                    />
                  ))}
                {/*<Placemark modules={["geoObject.addon.balloon"]} geometry={[55.684758, 37.738521]} properties={{balloonContentBody: 'ул. Рябиновая, вл.24, Троекуровское кладбище'}} />*/}
                <FullscreenControl />
                <GeolocationControl />
                <RouteButton />
              </YMap>
            </YMaps>
          </CMap>
        </CContainer>
      </BackgroundContainer>
    </>
  );
};

export default CompaniesPage;
